import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const sharingRows = [
  { key: 'evgOperation', name: 'EVG vận hành' },
  { key: 'premiseReferral', name: 'Giới thiệu MB' },
  { key: 'investorReferral', name: 'Giới thiệu CĐT' },
  { key: 'landlord', name: 'MB' },
  { key: 'investorMinimum', name: 'CĐT tối thiểu' },
];

const defaultSharingValues = {
  electricityCost: 0,
  evgOperation: 0,
  premiseReferral: 0,
  investorReferral: 0,
  landlord: 0,
  investor: 0,
  investorMinimum: 150,
};

const defaultSharingTables = [
  {
    key: 'MOTORBIKE',
    title: 'Xe máy',
    columns: [{ key: 'PRICE_0', price: 0, values: { ...defaultSharingValues } }],
  },
  {
    key: 'CAR',
    title: 'Xe ô tô',
    columns: [{ key: 'PRICE_0', price: 0, values: { ...defaultSharingValues } }],
  },
];

const defaultInputTieredPrice = [
  { upto: 50, price: 0 },
  { upto: 100, price: 0 },
  { upto: 200, price: 0 },
  { upto: 300, price: 0 },
  { upto: 400, price: 0 },
  { upto: 999999999, price: 0 },
];

const parseCurrencyNumber = (value, fallback = 0) => {
  if (_.isNumber(value)) return Number.isFinite(value) ? value : fallback;
  if (_.isNil(value)) return fallback;
  const normalized = String(value).replace(/[^\d-]/g, '');
  if (!normalized || normalized === '-') return fallback;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatMoney = (value) => parseCurrencyNumber(value).toLocaleString('vi-VN');

const cloneSharingTables = () =>
  defaultSharingTables.map((table) => ({
    ...table,
    columns: table.columns.map((column) => ({
      ...column,
      values: { ...column.values },
    })),
  }));

const normalizeSharingTables = (policy, fallbackPrice = 0) => {
  const sourceTables = Array.isArray(policy?.tables) ? policy.tables : [];
  const fallbackTables = cloneSharingTables();
  return fallbackTables.map((fallbackTable) => {
    const sourceTable = sourceTables.find((table) => table?.key === fallbackTable.key);
    const sourceColumn = sourceTable?.columns?.[0];
    const fallbackColumn = fallbackTable.columns[0];
    const price = parseCurrencyNumber(sourceColumn?.price, parseCurrencyNumber(fallbackPrice));
    return {
      ...fallbackTable,
      title: sourceTable?.title || fallbackTable.title,
      columns: [
        {
          ...fallbackColumn,
          ...sourceColumn,
          price,
          values: sharingRows.reduce(
            (result, row) => ({
              ...result,
              [row.key]: parseCurrencyNumber(
                sourceColumn?.values?.[row.key],
                fallbackColumn?.values?.[row.key] ?? (row.key === 'investorMinimum' ? 150 : 0)
              ),
            }),
            {}
          ),
        },
      ],
    };
  });
};

const buildEnergySharingPolicy = (tables) => ({
  schemaVersion: 'ENERGY_SHARING_V1',
  tables: tables.map((table) => ({
    key: table.key,
    title: table.title,
    columns: table.columns.map((column) => ({
      key: column.key,
      price: parseCurrencyNumber(column.price),
      values: sharingRows.reduce(
        (result, row) => ({
          ...result,
          [row.key]: parseCurrencyNumber(column.values?.[row.key]),
        }),
        {}
      ),
    })),
  })),
});

const getSharingTotal = (column) =>
  sharingRows.reduce((sum, row) => sum + parseCurrencyNumber(column?.values?.[row.key]), 0);

const allSharingTableKeys = defaultSharingTables.map((table) => table.key);

const getVehicleTypeKey = (vehicleType) => {
  if (vehicleType === ChargeConstants.VehicleType.CAR || vehicleType === 'CAR') return 'CAR';
  if (
    vehicleType === ChargeConstants.VehicleType.BIKE ||
    vehicleType === 'BIKE' ||
    vehicleType === 'MOTORBIKE'
  ) {
    return 'MOTORBIKE';
  }
  return null;
};

const getPointSharingTableKey = (point) =>
  getVehicleTypeKey(point?.vehicleType || point?.chargeModel?.vehicleType || point?.model?.vehicleType);

const getVehicleTypeName = (point) => {
  const key = getPointSharingTableKey(point);
  if (key === 'CAR') return 'Ô tô';
  if (key === 'MOTORBIKE') return 'Xe máy';
  return '';
};

const normalizeInputTieredPrice = (value) => {
  const source = Array.isArray(value) ? value : [];
  return defaultInputTieredPrice.map((tier, index) => ({
    upto: parseCurrencyNumber(source[index]?.upto, tier.upto),
    price: parseCurrencyNumber(source[index]?.price, tier.price),
  }));
};

const getFallbackInputPricing = (item) => {
  const source = item?.chargeStation || item?.station || item;
  return {
    inputPricingMode: source?.inputPricingMode || ChargeConstants.ChargePricingMode.FLAT,
    inputFlatPrice: source?.inputFlatPrice ?? 0,
    inputNormalPrice: source?.inputNormalPrice ?? 0,
    inputPeakPrice: source?.inputPeakPrice ?? 0,
    inputOffPeakPrice: source?.inputOffPeakPrice ?? 0,
    inputTieredPrice: normalizeInputTieredPrice(source?.inputTieredPrice),
  };
};

const getFallbackSalePricing = (item) => {
  const pricing =
    item?.pricing ||
    item?.chargeStation?.pricing ||
    item?.station?.pricing ||
    item;
  return normalizeSharingTables(pricing?.energySharingPolicy, pricing?.flatPrice);
};

DialogAddPricingView.propTypes = {
  item: PropTypes.object,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogAddPricingView({ item, onCancel, onDone }) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN';
  const editMode = !!item;
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [valuationObjectType, setValuationObjectType] = useState('CHARGE_POINT');
  const [selectedChargeStation, setSelectedChargeStation] = useState(null);
  const [stationSharingTableKeys, setStationSharingTableKeys] = useState([]);
  const dropdownChargeStationEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="dialog_stationId"
        name="dialog_stationId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargeStation?.name ?? 'Chọn trạm sạc'}
          </button>
        }
        selectedValue={selectedChargeStation}
        dropdownMenuItems={[{ name: 'Trạm sạc', role: 'CHARGE_STATION' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargeStationList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargeStations?.map((row) => ({
                id: row?.stationId,
                name: row?.stationCode ? `${row?.stationCode} - ${row?.name ?? ''}` : row?.name,
                item: row,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargeStation(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trạm sạc',
          role: 'CHARGE_STATION',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargeStation(null);
              setStationSharingTableKeys([]);
            })}
          </div>
        }
      />
    );
  }, [selectedChargeStation]);
  const [selectedChargePoint, setSelectedChargePoint] = useState(null);
  const dropdownChargePointEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="dialog_pointId"
        name="dialog_pointId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargePoint?.name ?? 'Chọn trụ sạc'}
          </button>
        }
        selectedValue={selectedChargePoint}
        dropdownMenuItems={[{ name: 'Trụ sạc', role: 'CHARGE_POINT' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargePointList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargePoints?.map((row) => ({
                id: row?.pointId,
                name: [row?.pointCode, getVehicleTypeName(row)].filter(Boolean).join(' - '),
                item: row,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargePoint(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trụ sạc',
          role: 'CHARGE_POINT',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargePoint(null);
            })}
          </div>
        }
      />
    );
  }, [selectedChargePoint]);
  const [pricingMode, setPricingMode] = useState(ChargeConstants.ChargePricingMode.FLAT);
  const [flatPrice, setFlatPrice] = useState('');
  const [normalPrice, setNormalPrice] = useState('');
  const [peakPrice, setPeakPrice] = useState('');
  const [offPeakPrice, setOffPeakPrice] = useState('');
  const [freeOverchargeDuration, setFreeOverchargeDuration] = useState('');
  const [overchargePrice, setOverchargePrice] = useState('');
  const [reservationFee, setReservationFee] = useState('');
  const [reservationFreeCancellation, setReservationFreeCancellation] = useState('');
  const [inputPricingMode, setInputPricingMode] = useState(ChargeConstants.ChargePricingMode.FLAT);
  const [inputFlatPrice, setInputFlatPrice] = useState('');
  const [inputNormalPrice, setInputNormalPrice] = useState('');
  const [inputPeakPrice, setInputPeakPrice] = useState('');
  const [inputOffPeakPrice, setInputOffPeakPrice] = useState('');
  const [inputTieredPrice, setInputTieredPrice] = useState(normalizeInputTieredPrice());
  const [commissionSharingTables, setCommissionSharingTables] = useState(cloneSharingTables());
  const visibleSharingTableKeys = useMemo(() => {
    if (valuationObjectType === 'CHARGE_POINT') {
      const pointKey = getPointSharingTableKey(selectedChargePoint?.item);
      return pointKey ? [pointKey] : allSharingTableKeys;
    }
    if (valuationObjectType === 'CHARGE_STATION') {
      return stationSharingTableKeys.length > 0 ? stationSharingTableKeys : allSharingTableKeys;
    }
    return allSharingTableKeys;
  }, [selectedChargePoint, stationSharingTableKeys, valuationObjectType]);

  const pricingMinimums = useMemo(() => {
    const tables = commissionSharingTables || [];
    const carTable = tables.find((t) => t.key === 'CAR') || tables[0];
    const bikeTable = tables.find((t) => t.key === 'MOTORBIKE') || tables[1];

    const getColumnFixedFee = (column) => {
      const vals = column?.values || {};
      const evg = parseCurrencyNumber(vals.evgOperation, 0);
      const premise = parseCurrencyNumber(vals.premiseReferral, 0);
      const invRef = parseCurrencyNumber(vals.investorReferral, 0);
      const landlord = parseCurrencyNumber(vals.landlord, 0);
      const invMin = parseCurrencyNumber(vals.investorMinimum, 150);
      return {
        total: evg + premise + invRef + landlord + invMin,
        evg,
        premise,
        invRef,
        landlord,
        invMin,
      };
    };

    const carBreakdown = getColumnFixedFee(carTable?.columns?.[0]);
    const bikeBreakdown = getColumnFixedFee(bikeTable?.columns?.[0]);

    const isCarVisible = visibleSharingTableKeys.includes('CAR');
    const isBikeVisible = visibleSharingTableKeys.includes('MOTORBIKE');
    // Breakdown dùng cho Alert Box & hint giá sàn: ưu tiên theo loại xe chính (CAR > MOTORBIKE)
    const activeBreakdown = isCarVisible ? carBreakdown : (bikeBreakdown || carBreakdown);
    // fixedFee dùng cho HOURS hint (giá bán là 1 giá chung): dùng phí của loại xe chính
    const fixedFee = activeBreakdown.total || 1100;

    const numFlatElec = parseCurrencyNumber(inputFlatPrice, 0);
    const numNormalElec = parseCurrencyNumber(inputNormalPrice, 0);
    const numPeakElec = parseCurrencyNumber(inputPeakPrice, 0);
    const numOffPeakElec = parseCurrencyNumber(inputOffPeakPrice, 0);

    let tieredBase = 0;
    if (Array.isArray(inputTieredPrice) && inputTieredPrice.length > 0) {
      const validPrices = inputTieredPrice
        .map((t) => parseCurrencyNumber(t?.price, 0))
        .filter((p) => p > 0);
      if (validPrices.length > 0) tieredBase = Math.max(...validPrices);
    }

    let resolvedInput = {
      flat: numFlatElec,
      normal: numNormalElec,
      peak: numPeakElec,
      offPeak: numOffPeakElec,
    };

    if (inputPricingMode === ChargeConstants.ChargePricingMode.HOURS) {
      const safeFlat = Math.max(numPeakElec, numNormalElec, numFlatElec);
      resolvedInput = {
        flat: safeFlat,
        normal: numNormalElec > 0 ? numNormalElec : safeFlat,
        peak: numPeakElec > 0 ? numPeakElec : safeFlat,
        offPeak: numOffPeakElec > 0 ? numOffPeakElec : (numNormalElec > 0 ? numNormalElec : safeFlat),
      };
    } else if (inputPricingMode === ChargeConstants.ChargePricingMode.TIERED) {
      const base = tieredBase > 0 ? tieredBase : numFlatElec;
      resolvedInput = { flat: base, normal: base, peak: base, offPeak: base };
    } else {
      resolvedInput = {
        flat: numFlatElec,
        normal: numNormalElec > 0 ? numNormalElec : numFlatElec,
        peak: numPeakElec > 0 ? numPeakElec : numFlatElec,
        offPeak: numOffPeakElec > 0 ? numOffPeakElec : numFlatElec,
      };
    }

    // byTable: map tableKey → giá sàn FLAT riêng cho từng loại xe (fix lỗi xe máy bị áp sàn ô tô)
    const byTable = {};
    tables.forEach((table) => {
      const breakdown = getColumnFixedFee(table?.columns?.[0]);
      byTable[table.key] = {
        fixedFee: breakdown.total,
        breakdown,
        minFlat: breakdown.total,
        minNormal: breakdown.total,
        minPeak: breakdown.total,
        minOffPeak: breakdown.total,
        suggestedFlat: resolvedInput.flat + breakdown.total,
        suggestedNormal: resolvedInput.normal + breakdown.total,
        suggestedPeak: resolvedInput.peak + breakdown.total,
        suggestedOffPeak: resolvedInput.offPeak + breakdown.total,
      };
    });

    return {
      fixedFee,
      breakdown: activeBreakdown,
      resolvedInput,
      byTable,
      minFlat: fixedFee,
      minNormal: fixedFee,
      minPeak: fixedFee,
      minOffPeak: fixedFee,
      suggestedFlat: resolvedInput.flat + fixedFee,
      suggestedNormal: resolvedInput.normal + fixedFee,
      suggestedPeak: resolvedInput.peak + fixedFee,
      suggestedOffPeak: resolvedInput.offPeak + fixedFee,
    };
  }, [
    commissionSharingTables,
    visibleSharingTableKeys,
    inputPricingMode,
    inputFlatPrice,
    inputNormalPrice,
    inputPeakPrice,
    inputOffPeakPrice,
    inputTieredPrice,
  ]);

  useEffect(() => {
    if (valuationObjectType !== 'CHARGE_STATION' || !selectedChargeStation?.id) {
      setStationSharingTableKeys([]);
      return;
    }

    let isMounted = true;
    ChargeHelper.API.requestGetChargePointList({
      page: 0,
      limit: 1000,
      stationId: selectedChargeStation.id,
    }).then((data) => {
      if (!isMounted) return;
      const keys = _.uniq(
        (data.chargePoints ?? []).map((point) => getPointSharingTableKey(point)).filter(Boolean)
      );
      setStationSharingTableKeys(keys);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedChargeStation?.id, valuationObjectType]);

  useEffect(() => {
    if (editMode) return;
    const source =
      valuationObjectType === 'CHARGE_POINT'
        ? selectedChargePoint?.item
        : selectedChargeStation?.item;
    if (!source) return;
    const inputPricing = getFallbackInputPricing(source);
    setInputPricingMode(inputPricing.inputPricingMode);
    setInputFlatPrice(inputPricing.inputFlatPrice);
    setInputNormalPrice(inputPricing.inputNormalPrice);
    setInputPeakPrice(inputPricing.inputPeakPrice);
    setInputOffPeakPrice(inputPricing.inputOffPeakPrice);
    setInputTieredPrice(inputPricing.inputTieredPrice);
    setCommissionSharingTables(getFallbackSalePricing(source));
  }, [editMode, selectedChargePoint, selectedChargeStation, valuationObjectType]);

  useEffect(() => {
    if (item) {
      if (!_.isNil(item?.stationId)) {
        setValuationObjectType('CHARGE_STATION');
        setSelectedChargeStation({
          id: item?.stationId,
          name: item?.station?.stationCode
            ? `${item?.station?.stationCode} - ${item?.station?.name ?? ''}`
            : item?.station?.name,
          item: item?.station,
        });
      } else if (!_.isNil(item?.pointId)) {
        setValuationObjectType('CHARGE_POINT');
        setSelectedChargePoint({
          id: item?.pointId,
          name: item?.point?.pointCode,
          item: item?.point,
        });
      } else {
        setValuationObjectType('CHARGE_POINT');
      }

      setPricingMode(item?.pricingMode);
      setFlatPrice(item?.flatPrice);
      setNormalPrice(item?.normalPrice);
      setPeakPrice(item?.peakPrice);
      setOffPeakPrice(item?.offPeakPrice);
      setFreeOverchargeDuration(item?.freeOverchargeDuration / 60);
      setOverchargePrice(item?.overchargePrice);
      setReservationFee(item?.reservationFee);
      setReservationFreeCancellation(item?.reservationFreeCancellation);
      const fallbackInputPricing = getFallbackInputPricing(item?.point || item?.station || item);
      setInputPricingMode(item?.inputPricingMode || fallbackInputPricing.inputPricingMode);
      setInputFlatPrice(item?.inputFlatPrice ?? fallbackInputPricing.inputFlatPrice);
      setInputNormalPrice(item?.inputNormalPrice ?? fallbackInputPricing.inputNormalPrice);
      setInputPeakPrice(item?.inputPeakPrice ?? fallbackInputPricing.inputPeakPrice);
      setInputOffPeakPrice(item?.inputOffPeakPrice ?? fallbackInputPricing.inputOffPeakPrice);
      setInputTieredPrice(normalizeInputTieredPrice(item?.inputTieredPrice ?? fallbackInputPricing.inputTieredPrice));
      setCommissionSharingTables(normalizeSharingTables(item?.energySharingPolicy, item?.flatPrice));
    } else {
      setCommissionSharingTables(cloneSharingTables());
    }
  }, [item]);

  // MARK: ---- METHODS ----
  const updateSharingColumn = (tableIndex, updater) => {
    setCommissionSharingTables((prev) =>
      prev.map((table, currentIndex) => {
        if (currentIndex !== tableIndex) return table;
        return {
          ...table,
          columns: table.columns.map((column, columnIndex) =>
            columnIndex === 0 ? updater(column) : column
          ),
        };
      })
    );
  };

  const updateInputTier = (index, value) => {
    setInputTieredPrice((prev) =>
      normalizeInputTieredPrice(prev).map((tier, currentIndex) =>
        currentIndex === index ? { ...tier, price: value } : tier
      )
    );
  };

  const handleSubmit = async () => {
    setReadyToValidate(true);

    if (!isAdmin) {
      ToastHelper.showError('Chỉ admin được cấu hình giá riêng cho trụ sạc hoặc trạm sạc');
      return;
    }
    if (valuationObjectType === 'CHARGE_POINT' && !selectedChargePoint) {
      ToastHelper.showError('Vui lòng chọn trụ sạc');
      return;
    }
    if (valuationObjectType === 'CHARGE_STATION' && !selectedChargeStation) {
      ToastHelper.showError('Vui lòng chọn trạm sạc');
      return;
    }
    if (!['CHARGE_POINT', 'CHARGE_STATION'].includes(valuationObjectType)) {
      ToastHelper.showError('Chỉ được cấu hình giá riêng cho trụ sạc hoặc trạm sạc');
      return;
    }
    if (!Utils.isValid(overchargePrice) || !Utils.isValid(freeOverchargeDuration)) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin cấu hình giá');
      return;
    }
    if (
      pricingMode === ChargeConstants.ChargePricingMode.HOURS &&
      (!Utils.isValid(normalPrice) || !Utils.isValid(peakPrice) || !Utils.isValid(offPeakPrice))
    ) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin giá điện');
      return;
    }
    if (!Utils.isValid(reservationFee) || !Utils.isValid(reservationFreeCancellation)) {
      ToastHelper.showError('Vui lòng nhập đầy đủ thông tin đặt chỗ trước');
      return;
    }
    if (
      inputPricingMode === ChargeConstants.ChargePricingMode.FLAT &&
      !Utils.isValid(inputFlatPrice)
    ) {
      ToastHelper.showError('Vui lòng nhập giá điện mua vào đồng giá');
      return;
    }
    if (
      inputPricingMode === ChargeConstants.ChargePricingMode.HOURS &&
      (!Utils.isValid(inputNormalPrice) || !Utils.isValid(inputPeakPrice) || !Utils.isValid(inputOffPeakPrice))
    ) {
      ToastHelper.showError('Vui lòng nhập đầy đủ giá điện mua vào theo khung giờ');
      return;
    }
    if (
      inputPricingMode === ChargeConstants.ChargePricingMode.TIERED &&
      normalizeInputTieredPrice(inputTieredPrice).some((tier) => !Utils.isValid(tier.price))
    ) {
      ToastHelper.showError('Vui lòng nhập đầy đủ giá điện mua vào theo bậc thang');
      return;
    }
    const scopedSharingTables = commissionSharingTables.filter((table) =>
      visibleSharingTableKeys.includes(table.key)
    );
    const energySharingPolicyParam = buildEnergySharingPolicy(scopedSharingTables);
    const invalidSharing = energySharingPolicyParam.tables.some((table) => {
      const column = table.columns?.[0];
      return (
        !column ||
        column.price < 0 ||
        sharingRows.some((row) => parseCurrencyNumber(column.values?.[row.key]) < 0)
      );
    });
    if (invalidSharing) {
      ToastHelper.showError('Vui lòng nhập cấu hình chia hoa hồng hợp lệ');
      return;
    }

    // Kiểm tra giá bán không được thấp hơn giá tối thiểu (chưa bao gồm tiền điện)
    if (pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
      const numNormal = parseCurrencyNumber(normalPrice);
      const numPeak = parseCurrencyNumber(peakPrice);
      const numOffPeak = parseCurrencyNumber(offPeakPrice);

      if (numNormal < 0 || numPeak < 0 || numOffPeak < 0) {
        ToastHelper.showError('Giá bán các khung giờ không được âm');
        return;
      }

      if (numNormal < pricingMinimums.minNormal) {
        ToastHelper.showError(
          `Giá giờ bình thường (${formatMoney(numNormal)} đ) không được thấp hơn giá tối thiểu ${formatMoney(pricingMinimums.minNormal)} đ/kWh (gồm phí DV EVG và các phần hoa hồng: ${formatMoney(pricingMinimums.fixedFee)} đ - chưa bao gồm tiền điện)`
        );
        return;
      }
      if (numPeak < pricingMinimums.minPeak) {
        ToastHelper.showError(
          `Giá giờ cao điểm (${formatMoney(numPeak)} đ) không được thấp hơn giá tối thiểu ${formatMoney(pricingMinimums.minPeak)} đ/kWh (gồm phí DV EVG và các phần hoa hồng: ${formatMoney(pricingMinimums.fixedFee)} đ - chưa bao gồm tiền điện)`
        );
        return;
      }
      if (numOffPeak < pricingMinimums.minOffPeak) {
        ToastHelper.showError(
          `Giá giờ thấp điểm (${formatMoney(numOffPeak)} đ) không được thấp hơn giá tối thiểu ${formatMoney(pricingMinimums.minOffPeak)} đ/kWh (gồm phí DV EVG và các phần hoa hồng: ${formatMoney(pricingMinimums.fixedFee)} đ - chưa bao gồm tiền điện)`
        );
        return;
      }
    } else if (pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
      // Mỗi loại xe (CAR, MOTORBIKE) so sánh với giá sàn riêng của chính bảng đó
      const invalidFlatTable = scopedSharingTables.find((table) => {
        const col = table.columns?.[0];
        const price = parseCurrencyNumber(col?.price);
        const tableMin = pricingMinimums.byTable[table.key]?.minFlat ?? pricingMinimums.minFlat;
        return price < tableMin;
      });
      if (invalidFlatTable) {
        const tableMin =
          pricingMinimums.byTable[invalidFlatTable.key]?.minFlat ?? pricingMinimums.minFlat;
        ToastHelper.showError(
          `Giá sạc cố định cho ${invalidFlatTable.title} (${formatMoney(invalidFlatTable.columns?.[0]?.price)} đ) không được thấp hơn giá tối thiểu ${formatMoney(tableMin)} đ/kWh (gồm phí DV EVG và các phần hoa hồng: ${formatMoney(tableMin)} đ - chưa bao gồm tiền điện)`
        );
        return;
      }
    }

    let params = {
      pricingMode: pricingMode,
      freeOverchargeDuration: _.toNumber(freeOverchargeDuration) * 60,
      overchargePrice: overchargePrice,
      reservationFee: reservationFee,
      reservationFreeCancellation: reservationFreeCancellation,
      currency: 'VNĐ',
      energySharingPolicy: energySharingPolicyParam,
      inputPricingMode,
      inputFlatPrice: inputPricingMode === ChargeConstants.ChargePricingMode.FLAT ? inputFlatPrice : 0,
      inputNormalPrice: inputPricingMode === ChargeConstants.ChargePricingMode.HOURS ? inputNormalPrice : 0,
      inputPeakPrice: inputPricingMode === ChargeConstants.ChargePricingMode.HOURS ? inputPeakPrice : 0,
      inputOffPeakPrice: inputPricingMode === ChargeConstants.ChargePricingMode.HOURS ? inputOffPeakPrice : 0,
      inputTieredPrice:
        inputPricingMode === ChargeConstants.ChargePricingMode.TIERED
          ? normalizeInputTieredPrice(inputTieredPrice).map((tier) => ({
            upto: parseCurrencyNumber(tier.upto),
            price: parseCurrencyNumber(tier.price),
          }))
          : [],
    };
    if (pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
      params.flatPrice = energySharingPolicyParam.tables?.[0]?.columns?.[0]?.price ?? flatPrice;
    } else if (pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
      params.normalPrice = normalPrice;
      params.peakPrice = peakPrice;
      params.offPeakPrice = offPeakPrice;
    }
    if (valuationObjectType === 'CHARGE_STATION') {
      params.stationId = selectedChargeStation?.id;
    } else if (valuationObjectType === 'CHARGE_POINT') {
      params.pointId = selectedChargePoint?.id;
    }
    // console.log(params);
    // return;
    setLoading(true);
    if (!editMode) {
      const res = await ChargeHelper.API.requestCreateChargePricing(params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Thêm mới cấu hình giá thành công');
        onDone && onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    } else {
      const res = await ChargeHelper.API.requestUpdateChargePricing(item?.pricingId, params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Cập nhật cấu hình giá thành công');
        onDone && onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="px-4 mt-4">
        {UIHelper.buildAlert(
          <i
            className="fa-duotone fa-circle-info fa-2x"
            style={{ color: 'rgba(41, 132, 238, 1)' }}
          />,
          'Đối tượng định giá',
          <>
            Chọn{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              “Trụ sạc”{' '}
            </span>
            để thiết lập mức giá và chia hoa hồng áp dụng cho riêng một trụ sạc. Hệ thống chỉ
            hiển thị đúng loại xe của trụ đó. <br />
            Chọn{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              “Trạm sạc”{' '}
            </span>
            để thiết lập mức giá và chia hoa hồng áp dụng cho toàn bộ trụ sạc thuộc một trạm,
            chỉ theo các loại xe đang có trong trạm.
          </>,
          'rgba(41, 132, 238, 1)',
          'rgba(246, 252, 255, 1)'
        )}
      </div>

      {/* doi tuong dinh gia */}
      {isAdmin && (
        <div className="mt-4">
          {UIHelper.buildCardHeader('Đối tượng định giá', false, null, true)}
          <div className="radio-inline w-100 px-5 mb-4">
            <div className="d-flex align-items-center gap-5">
              <KTRadio
                name="valuationObjectPoint"
                label="Trụ sạc"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('CHARGE_POINT');
                }}
                checked={valuationObjectType === 'CHARGE_POINT'}
              />
              <KTRadio
                name="valuationObjectStation"
                label="Trạm sạc"
                type={KTRadioType.outline}
                additionalClassName="radio-success"
                onChange={(value) => {
                  setValuationObjectType('CHARGE_STATION');
                }}
                checked={valuationObjectType === 'CHARGE_STATION'}
              />
            </div>
          </div>
        </div>
      )}
      {isAdmin && (
        <div
          className="row m-0 p-0 px-2 mt-4"
          style={{
            borderBottom: `1px dashed ${AppColors.gray[300]}`,
          }}
        >
          <div className="col-lg-6 mb-4 px-2">
            {valuationObjectType === 'CHARGE_POINT' && (
              <div>
                {UIHelper.buildFormLabel('Trụ sạc', true)}
                {dropdownChargePointEl}
              </div>
            )}
            {valuationObjectType === 'CHARGE_STATION' && (
              <div>
                {UIHelper.buildFormLabel('Trạm sạc', true)}
                {dropdownChargeStationEl}
              </div>
            )}
          </div>
        </div>
      )}

      {/* cau hinh gia */}
      <div className="mt-2">{UIHelper.buildCardHeader('Cấu hình giá', false, null, true)}</div>
      <div
        className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Đơn giá sạc quá giờ', true, false)}
              <KTTooltip
                text={`<div>Là số chi phí khách hàng phải trả cho mỗi phút chưa rút vòi sạc ra khỏi xe và đặt lại về trụ sạc</div>`}
                textHeader="Đơn giá sạc quá giờ "
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="VNĐ/phút"
              placeholder="Nhập đơn giá sạc quá giờ"
              name="overchargePrice"
              value={overchargePrice}
              onChange={(value) => {
                setOverchargePrice(value);
              }}
            />
          </div>
        </div>
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Thời gian chờ miễn phí', true, false)}
              <KTTooltip
                text={`<div>Thời gian chờ miễn phí là thời gian sau khi xe đã sạc đầy nhưng chủ xe chưa đến hoàn thành rút vòi sạc ra khỏi xe và đặt lại về trụ sạc</div>`}
                textHeader="Thời gian chờ miễn phí "
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="Phút"
              placeholder="Nhập thời gian chờ miễn phí"
              name="freeOverchargeDuration"
              value={freeOverchargeDuration}
              onChange={(value) => {
                setFreeOverchargeDuration(value);
              }}
            />
          </div>
        </div>
      </div>

      {/* co che tinh gia dien */}
      <div className="mt-2">
        {UIHelper.buildCardHeader('Cơ chế tính giá sạc', false, null, true)}
      </div>
      <div className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        <div className="col-lg-6 mb-4 px-2">
          <div>
            {UIHelper.buildFormLabel('Phương thức tính giá', true)}
            <KTBSDropdown
              name="pricingMode"
              toggleElement={
                <button
                  className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                  type="button"
                >
                  {pricingMode
                    ? _.find(
                      ChargeHelper.Data.chargePointPricingModeOptions,
                      (item) => item?.value === pricingMode
                    )?.name
                    : 'Chọn cách tính giá điện'}
                </button>
              }
              selectedValue={pricingMode}
              dropdownMenuItems={ChargeHelper.Data.chargePointPricingModeOptions}
              dropdownMenuClassName="overflow-auto w-100"
              dropdownMenuStyle={{ maxHeight: '30vh' }}
              onChange={(value) => {
                setPricingMode(value);
              }}
              isDropdownForm={true}
            />
          </div>
        </div>

        {pricingMode === ChargeConstants.ChargePricingMode.HOURS && (
          <>
            <div className="col-lg-6 mb-4 px-2">
              <div>
                {/* gia gio binh thuong */}
                <div>
                  <div className='d-flex gap-2'>
                    {UIHelper.buildFormLabel('Giá giờ bình thường', true, false)}
                    <KTTooltip
                      text={`<div><div>• Từ thứ 2 đến thứ 7: 04:00 - 09:30, 11:30 - 17:00, 20:00 - 22:00</div>
                            <div>• Chủ nhật: 04:00 - 22:00</div></div>`}
                      textHeader="Giờ bình thường"
                      isHtmlContent
                    >
                      <i
                        className="fa-regular fa-info-circle"
                        style={{ color: AppColors.info }}
                      />
                    </KTTooltip>
                  </div>
                  <KTFormInput
                    type={KTFormInputType.currency}
                    unit="VNĐ/kWh"
                    placeholder="Nhập giá giờ bình thường"
                    name="normalPrice"
                    value={normalPrice}
                    onChange={(value) => {
                      setNormalPrice(value);
                    }}
                  />
                </div>
              </div>
            </div>
            {/* gia gio cao diem */}
            <div className="col-lg-6 mb-4 px-2">
              <div>
                <div className='d-flex gap-2'>
                  {UIHelper.buildFormLabel('Giá giờ cao điểm', true, false)}
                  <KTTooltip
                    text={`<div><div>Từ thứ 2 đến thứ 7: 09:30 - 11:30, 17:00 - 20:00</div>
                        <div>Chủ nhật: không có</div></div>`}
                    textHeader="Giờ cao điểm"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.info }}
                    />
                  </KTTooltip>
                </div>
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập giá giờ cao điểm"
                  name="peakPrice"
                  value={peakPrice}
                  onChange={(value) => {
                    setPeakPrice(value);
                  }}
                />
              </div>
            </div>
            {/* gia gio thap diem */}
            <div className="col-lg-6 mb-4 px-2">
              <div>
                <div className='d-flex gap-2'>
                  {UIHelper.buildFormLabel('Giá giờ thấp điểm', true, false)}
                  <KTTooltip
                    text={`<div>Từ 22:00 - 04:00 tất cả các ngày trong tuần</div>`}
                    textHeader="Giờ thấp điểm"
                    isHtmlContent
                  >
                    <i
                      className="fa-regular fa-info-circle"
                      style={{ color: AppColors.info }}
                    />
                  </KTTooltip>
                </div>
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập giá giờ thấp điểm"
                  name="offPeakPrice"
                  value={offPeakPrice}
                  onChange={(value) => {
                    setOffPeakPrice(value);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* gia dien mua vao */}
      <div className="mt-2">
        {UIHelper.buildCardHeader('Giá điện mua vào', false, null, true)}
      </div>
      <div
        className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        <div className="col-lg-6 mb-4 px-2">
          <div>
            {UIHelper.buildFormLabel('Phương thức tính giá điện mua vào', true)}
            <KTBSDropdown
              name="inputPricingMode"
              toggleElement={
                <button
                  className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                  type="button"
                >
                  {_.find(
                    ChargeHelper.Data.chargeStationPricingModeOptions,
                    (item) => item?.value === inputPricingMode
                  )?.name ?? 'Chọn phương thức'}
                </button>
              }
              selectedValue={inputPricingMode}
              dropdownMenuItems={ChargeHelper.Data.chargeStationPricingModeOptions}
              dropdownMenuClassName="overflow-auto w-100"
              dropdownMenuStyle={{ maxHeight: '30vh' }}
              onChange={(value) => {
                setInputPricingMode(value);
              }}
              isDropdownForm={true}
            />
          </div>
        </div>

        {inputPricingMode === ChargeConstants.ChargePricingMode.FLAT && (
          <div className="col-lg-6 mb-4 px-2">
            {UIHelper.buildFormLabel('Giá điện mua vào cố định', true)}
            <KTFormInput
              type={KTFormInputType.currency}
              unit="VNĐ/kWh"
              placeholder="Nhập giá điện mua vào"
              name="inputFlatPrice"
              value={inputFlatPrice}
              onChange={(value) => {
                setInputFlatPrice(value);
              }}
            />
          </div>
        )}

        {inputPricingMode === ChargeConstants.ChargePricingMode.HOURS && (
          <>
            <div className="col-lg-6 mb-4 px-2">
              <div className="d-flex gap-2">
                {UIHelper.buildFormLabel('Giá giờ bình thường', true, false)}
                <KTTooltip
                  text={`<div><div>• Từ thứ 2 đến thứ 7: 04:00 - 09:30, 11:30 - 17:00, 20:00 - 22:00</div>
                        <div>• Chủ nhật: 04:00 - 22:00</div></div>`}
                  textHeader="Giờ bình thường"
                  isHtmlContent
                >
                  <i
                    className="fa-regular fa-info-circle"
                    style={{ color: AppColors.info }}
                  />
                </KTTooltip>
              </div>
              <KTFormInput
                type={KTFormInputType.currency}
                unit="VNĐ/kWh"
                placeholder="Nhập giá giờ bình thường"
                name="inputNormalPrice"
                value={inputNormalPrice}
                onChange={(value) => {
                  setInputNormalPrice(value);
                }}
              />
            </div>
            <div className="col-lg-6 mb-4 px-2">
              <div className="d-flex gap-2">
                {UIHelper.buildFormLabel('Giá giờ cao điểm', true, false)}
                <KTTooltip
                  text={`<div><div>Từ thứ 2 đến thứ 7: 09:30 - 11:30, 17:00 - 20:00</div>
                      <div>Chủ nhật: không có</div></div>`}
                  textHeader="Giờ cao điểm"
                  isHtmlContent
                >
                  <i
                    className="fa-regular fa-info-circle"
                    style={{ color: AppColors.info }}
                  />
                </KTTooltip>
              </div>
              <KTFormInput
                type={KTFormInputType.currency}
                unit="VNĐ/kWh"
                placeholder="Nhập giá giờ cao điểm"
                name="inputPeakPrice"
                value={inputPeakPrice}
                onChange={(value) => {
                  setInputPeakPrice(value);
                }}
              />
            </div>
            <div className="col-lg-6 mb-4 px-2">
              <div className="d-flex gap-2">
                {UIHelper.buildFormLabel('Giá giờ thấp điểm', true, false)}
                <KTTooltip
                  text={`<div>Từ 22:00 - 04:00 tất cả các ngày trong tuần</div>`}
                  textHeader="Giờ thấp điểm"
                  isHtmlContent
                >
                  <i
                    className="fa-regular fa-info-circle"
                    style={{ color: AppColors.info }}
                  />
                </KTTooltip>
              </div>
              <KTFormInput
                type={KTFormInputType.currency}
                unit="VNĐ/kWh"
                placeholder="Nhập giá giờ thấp điểm"
                name="inputOffPeakPrice"
                value={inputOffPeakPrice}
                onChange={(value) => {
                  setInputOffPeakPrice(value);
                }}
              />
            </div>
          </>
        )}

        {inputPricingMode === ChargeConstants.ChargePricingMode.TIERED && (
          <div className="col-12 px-2">
            <div className="row">
              {normalizeInputTieredPrice(inputTieredPrice).map((tier, index, array) => {
                const prevUpto = index > 0 ? array[index - 1]?.upto : 0;
                const label =
                  index === array.length - 1
                    ? `Trên ${formatMoney(prevUpto)} kWh`
                    : `${formatMoney(prevUpto)} - ${formatMoney(tier.upto)} kWh`;
                return (
                  <div className="col-lg-4 mb-4 px-2" key={tier.upto}>
                    {UIHelper.buildFormLabel(label, true)}
                    <KTFormInput
                      type={KTFormInputType.currency}
                      unit="VNĐ/kWh"
                      placeholder="Nhập giá bậc thang"
                      name={`inputTieredPrice_${index}`}
                      value={tier.price}
                      onChange={(value) => {
                        updateInputTier(index, value);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* cau hinh chia hoa hong */}
      <div className="mt-2">
        {UIHelper.buildCardHeader('Giá bán ra & chia hoa hồng', false, null, true)}
      </div>
      <div
        className="row m-0 p-0 px-2"
        style={{
          borderBottom: `1px dashed ${AppColors.gray[300]}`,
        }}
      >
        {commissionSharingTables.map((table, tableIndex) => {
          if (!visibleSharingTableKeys.includes(table.key)) return null;
          const column = table.columns?.[0] ?? {};
          const price = parseCurrencyNumber(column.price);
          const total = getSharingTotal(column);
          const investorRemain = Math.max(price - total, 0);
          return (
            <div className="col-lg-6 mb-4 px-2" key={table.key}>
              <div
                className="border rounded p-3 h-100"
                style={{ borderColor: AppColors.gray[300] }}
              >
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[700])}>
                    {table.title}
                  </span>
                </div>
                {pricingMode === ChargeConstants.ChargePricingMode.FLAT && (
                  <div className="mb-3">
                    {UIHelper.buildFormLabel(`Giá tổng ${table.title === 'Xe ô tô' ? 'ô tô' : 'xe máy'}`, true)}
                    <KTFormInput
                      type={KTFormInputType.currency}
                      unit="VNĐ/kWh"
                      placeholder={`Nhập giá tổng ${table.title === 'Xe ô tô' ? 'ô tô' : 'xe máy'}`}
                      name={`sharingPrice_${table.key}`}
                      value={column.price}
                      onChange={(value) => {
                        updateSharingColumn(tableIndex, (currentColumn) => ({
                          ...currentColumn,
                          price: value,
                        }));
                      }}
                    />
                    {(() => {
                      const tableMin =
                        pricingMinimums.byTable[table.key]?.minFlat ?? pricingMinimums.minFlat;
                      const tableFixedFee =
                        pricingMinimums.byTable[table.key]?.fixedFee ?? pricingMinimums.fixedFee;
                      return (
                        <div className="d-flex align-items-center justify-content-between mt-1 px-1">
                          <span className="text-muted small">
                            Tối thiểu: <span className="fw-bold" style={{ color: AppColors.danger }}>{formatMoney(tableMin)} VNĐ/kWh</span>
                          </span>
                          <span className="text-muted small">
                            (Điện: {formatMoney(pricingMinimums.resolvedInput.flat)}đ + Phí: {formatMoney(tableFixedFee)}đ)
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {sharingRows.map((row) => (
                  <div className="mb-3" key={row.key}>
                    {UIHelper.buildFormLabel(row.name, false)}
                    <KTFormInput
                      type={KTFormInputType.currency}
                      unit="VNĐ/kWh"
                      placeholder={`Nhập ${row.name.toLowerCase()}`}
                      name={`sharing_${table.key}_${row.key}`}
                      value={column.values?.[row.key]}
                      onChange={(value) => {
                        updateSharingColumn(tableIndex, (currentColumn) => ({
                          ...currentColumn,
                          values: {
                            ...(currentColumn.values ?? {}),
                            [row.key]: value,
                          },
                        }));
                      }}
                    />
                  </div>
                ))}
                {pricingMode === ChargeConstants.ChargePricingMode.FLAT && (
                  <div className="mb-3">
                    {UIHelper.buildFormLabel('CĐT nhận thực tế (tối thiểu + thặng dư)', false)}
                    <KTFormInput
                      type={KTFormInputType.currency}
                      unit="VNĐ/kWh"
                      name={`sharing_${table.key}_investor`}
                      value={parseCurrencyNumber(column.values?.investorMinimum, 150) + investorRemain}
                      disabled
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* dat cho truoc */}
      <div className="mt-2">{UIHelper.buildCardHeader('Đặt chỗ trước', false, null, true)}</div>
      <div className="row m-0 p-0 px-2">
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Phí đặt giữ chỗ', true, false)}
              <KTTooltip
                text={`<div>Chi phí khi tài xế hủy giữ chỗ sau thời gian hủy miễn phí hoặc không đến sạc.</div>`}
                textHeader="Phí đặt giữ chỗ"
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="VNĐ"
              placeholder="Nhập phí đặt giữ chỗ"
              name="reservationFee"
              value={reservationFee}
              onChange={(value) => {
                setReservationFee(value);
              }}
            />
          </div>
        </div>
        <div className="col-lg-6 mb-4 px-2">
          <div>
            <div className='d-flex gap-2'>
              {UIHelper.buildFormLabel('Thời gian hủy giữ chỗ miễn phí', true, false)}
              <KTTooltip
                text={`<div>Thời gian tài xế được hủy giữ chỗ mà không mất phí đặt giữ chỗ, tính từ thời điểm đặt chỗ thành công (phút)</div>`}
                textHeader="Thời gian hủy giữ chỗ miễn phí"
                isHtmlContent
              >
                <i
                  className="fa-regular fa-info-circle"
                  style={{ color: AppColors.info }}
                />
              </KTTooltip>
            </div>
            <KTFormInput
              type={KTFormInputType.currency}
              unit="Phút"
              placeholder="Nhập thời gian hủy giữ chỗ miễn phí"
              name="reservationFreeCancellation"
              value={reservationFreeCancellation}
              onChange={(value) => {
                setReservationFreeCancellation(value);
              }}
            />
          </div>
        </div>
      </div>

      {/* actions */}
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null)}
        {UIHelper.buildGreenButton(
          !editMode ? 'Thêm' : 'Cập nhật',
          handleSubmit,
          null,
          false,
          loading
        )}
      </div>
    </div>
  );
}

export default DialogAddPricingView;
